<template>
  <header class="top-bar">
    <div
      class="top-bar__bg"
      aria-hidden="true"
      :style="{ backgroundImage: `url(${headerBg})` }"
    />

    <div class="top-bar__inner">
      <div class="top-bar__side top-bar__side--left">
        <nav class="nav">
          <router-link
            v-for="p in pages"
            :key="p.id"
            :to="p.path"
            class="nav__item"
            :class="{ 'nav__item--on': p.id === activeId }"
          >
            <i class="nav__mark" aria-hidden="true" />
            <span class="nav__name">{{ p.name }}</span>
          </router-link>
        </nav>
      </div>

      <div class="top-bar__brand">
        <h1>数字水务</h1>
      </div>

      <div class="top-bar__side top-bar__side--right">
        <time class="top-bar__clock">{{ clockText }}</time>
      </div>
    </div>
  </header>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { PAGES } from "@/cesium/pageLayers";
import headerBg from "@/assets/image/headerbg.png";

defineProps({
  activeId: { type: String, required: true },
});

const pages = PAGES;

const clockText = ref("");
let timer = null;

function tick() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  clockText.value = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(
    d.getDate()
  )}  ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

onMounted(() => {
  tick();
  timer = setInterval(tick, 1000);
});
onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  height: 78px;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(2, 10, 20, 0.96) 0%,
    rgba(2, 12, 22, 0.9) 55%,
    rgba(2, 12, 22, 0.55) 85%,
    transparent 100%
  );
}

.top-bar__bg {
  position: absolute;
  inset: 0;
  top: 30px;
  background-position: center top;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  pointer-events: none;
  opacity: 0.95;
}

.top-bar__inner {
  position: relative;
  z-index: 1;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 36px 10px;
  box-sizing: border-box;
}

.top-bar__brand {
  text-align: center;
  padding: 0 32px;
  transform: translateY(-6px);
}

.top-bar__brand h1 {
  font-family: "Noto Sans SC", "PingFang SC", sans-serif;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.28em;
  color: #eafcff;
  text-indent: 0.28em;
  text-shadow:
    0 0 14px rgba(0, 220, 255, 0.7),
    0 0 32px rgba(0, 160, 220, 0.35);
  white-space: nowrap;
}

.top-bar__side {
  display: flex;
  align-items: center;
  gap: 20px;
  pointer-events: auto;
  min-width: 0;
  transform: translateY(-2px);
}

.top-bar__side--left {
  justify-content: flex-start;
  padding-left: 4%;
}

.top-bar__side--right {
  justify-content: flex-end;
  padding-right: 2%;
}

.top-bar__clock {
  font-family: "Orbitron", monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: rgba(140, 210, 230, 0.75);
  flex-shrink: 0;
  width: 168px;
  text-align: right;
}

.nav {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.nav__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 10px;
  text-decoration: none;
  color: rgba(170, 210, 225, 0.62);
  transition: color 0.22s ease;
  white-space: nowrap;
}

.nav__item::after {
  content: "";
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 2px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 220, 255, 0.85),
    transparent
  );
  opacity: 0;
  transform: scaleX(0.4);
  transition: opacity 0.22s ease, transform 0.22s ease;
  box-shadow: 0 0 8px rgba(0, 220, 255, 0.55);
}

.nav__mark {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(0, 200, 230, 0.35);
  box-shadow: none;
  flex-shrink: 0;
  transition: background 0.22s, box-shadow 0.22s, transform 0.22s;
}

.nav__name {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-indent: 0.18em;
  line-height: 1;
}

.nav__item:hover {
  color: rgba(220, 245, 255, 0.95);
}

.nav__item:hover .nav__mark {
  background: rgba(0, 230, 255, 0.85);
  box-shadow: 0 0 8px rgba(0, 220, 255, 0.7);
}

.nav__item:hover::after {
  opacity: 0.55;
  transform: scaleX(0.75);
}

.nav__item--on {
  color: #fff;
  text-shadow: 0 0 12px rgba(0, 220, 255, 0.45);
}

.nav__item--on .nav__mark {
  background: #5ef0ff;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.9);
  transform: scale(1.15);
}

.nav__item--on::after {
  opacity: 1;
  transform: scaleX(1);
}

@media (max-width: 1200px) {
  .top-bar__side--left {
    padding-left: 0;
  }
  .top-bar__brand h1 {
    font-size: 22px;
    letter-spacing: 0.18em;
    text-indent: 0.18em;
  }
  .nav__item {
    padding: 8px 12px 10px;
  }
  .nav__name {
    font-size: 13px;
    letter-spacing: 0.1em;
    text-indent: 0.1em;
  }
  .top-bar__clock {
    display: none;
  }
}
</style>
